import { useMemo, useRef } from 'react';

import { uploadArraySelector } from '@air/redux-uploader';

import {
  getLatestFinishedUploadBatch,
  rememberWatchedUploadBatches,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/getLatestFinishedUploadBatch';
import { type FinishedUploadBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { privateUploadMetadataSelector } from '~/store/privateUploadMetadata/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

const finishedUploadBatchesEqual = (left: FinishedUploadBatch | null, right: FinishedUploadBatch | null): boolean =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.batchId === right.batchId &&
    left.finishedAt === right.finishedAt &&
    left.attachments.length === right.attachments.length &&
    left.attachments.every(
      (attachment, index) =>
        attachment.id === right.attachments[index].id &&
        attachment.thumbnailUrl === right.attachments[index].thumbnailUrl,
    ));

export const usePrivateFinishedUploadBatch = (): { finishedUploadBatch: FinishedUploadBatch | null } => {
  const allUploads = useAirSelector(uploadArraySelector);
  const uploadsMetadata = useAirSelector(privateUploadMetadataSelector);
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  // The uploader is app-global, so a batch started in another workspace must never become this one's scope.
  const uploads = useMemo(
    () => allUploads.filter((upload) => workspaceId && uploadsMetadata[upload.id]?.workspaceId === workspaceId),
    [allUploads, uploadsMetadata, workspaceId],
  );
  const watchedBatchIdsRef = useRef(new Set<string>());
  const stableBatchRef = useRef<FinishedUploadBatch | null>(null);

  const finishedUploadBatch = useMemo(() => {
    rememberWatchedUploadBatches(watchedBatchIdsRef.current, uploads);
    const next = getLatestFinishedUploadBatch(uploads, watchedBatchIdsRef.current);
    const previous = stableBatchRef.current;

    if (finishedUploadBatchesEqual(next, previous)) {
      return previous;
    }

    stableBatchRef.current = next;

    return next;
  }, [uploads]);

  return { finishedUploadBatch };
};
